import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../hooks/useAuth'
import { societyQueries } from '../../../api/societies'
import { useSelectedSocietyId } from '../../../hooks/useSelectedSocietyContext'
import { unitQueries } from '../../../api/units'
import { widgetQueries } from '../../../api/society-widgets'
import { reverseUrl } from '../../reverseUrl'

export const useSocietyLoader = (id: string | undefined): void => {
  const { userId } = useAuth()
  const navigate = useNavigate()

  const { data, isLoading, isFetching } = useQuery(
    societyQueries.getUserSocieties(userId)
  )
  const { setCurrentSocietyId, selectedSocietyId } = useSelectedSocietyId()

  useEffect(() => {
    if (userId && !isLoading) {
      if (!id) {
        if (selectedSocietyId) return
        if (data) {
          const firstSociety = data[0]
          firstSociety && setCurrentSocietyId(firstSociety._id)
        }
      } else if (data && !isFetching && !data.some(({ _id }) => _id === id)) {
        // A removal from another session drops the society from the list
        // while its URL and cached views are still open. Going straight to the
        // next society matters: the navigation commits after this render, so
        // the old SocietyRoot can still select its society again on the way out.
        const nextSocietyId = data[0]?._id
        setCurrentSocietyId(nextSocietyId)
        navigate(
          nextSocietyId
            ? reverseUrl('society:detail', { id: nextSocietyId })
            : reverseUrl('society'),
          { replace: true }
        )
      } else {
        setCurrentSocietyId(id)
      }
    }
  }, [
    data,
    id,
    isFetching,
    isLoading,
    navigate,
    setCurrentSocietyId,
    selectedSocietyId,
    userId,
  ])

  useQuery(unitQueries.getUserUnits(userId))
  useQuery(widgetQueries.getWidgetsPerSociety(id))
}
